import React, { useEffect, useState } from 'react';
import {
  TextField, Checkbox, Select, MenuItem, FormControlLabel, Autocomplete, Button, FormControl, InputLabel, Switch, IconButton
} from '@mui/material';
import styled from 'styled-components';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CalendarIcon, LocalizationProvider } from '@mui/x-date-pickers';
import moment, { MomentInput } from 'moment';
import { Replay, VerifiedUser } from '@mui/icons-material';
import { emailRegex } from './DynamicFormNew';


const Form = styled.form`
  display:flex;
  gap:20px; 
  flex-direction:column; 
`

const Fieldset = styled.div`
  display:flex;
  gap:20px;  
  flex-wrap:wrap;
`

export type TFieldType = 'number' | 'text' | 'password' | 'select' | 'switch' | 'checkbox' | 'autocomplete' | 'date' | 'comment' | 'confirmationField'
export type TFieldValidation = {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  contain?: string[] | number[] | string;
  regex?: RegExp;
  email?:boolean;
}
// export interface IFieldConfig {
//   fieldName: string;
//   label: string;
//   fieldType: TFieldType;
//   isVisible?: boolean;
//   fullWidth?: boolean;
//   width?: string;
//   disabled?: boolean;
//   options?: { label: string, value: any }[];
//   onChange?: (event: Event, data: any) => {};
//   onConfirm?: (event: Event, data: any) => any | undefined;
//   value?: any;
//   isRequired?: boolean
//   initialValue?: any;
//   validation?: IFieldValidation;
// }
export type TField<T> = {
  fieldName: keyof T;
  label: string;
  fieldType: TFieldType;
  isVisible?: boolean;
  fullWidth?: boolean;
  width?: string;
  disabled?: boolean;
  value?: any;
  isRequired?: boolean
  initialValue?: any;
  validation?: TFieldValidation;
  isVerified?: boolean;
  options?: { label: string, value: any }[];
  onChange?: (value: any, data: T, setFormConfig:React.Dispatch<React.SetStateAction<TField<T>[]>>) => any;
  onConfirm?: (value: any, data: T) => any
}

export type TFormError = {
  fieldName: string,
  error: string
}

type TDynamicFieldProps<T> = {
  config: TField<T>;
  formData: T;
  onChange: (value: any) => any
  error: string;
}

type TDynamicFormProps<T> = {
  config: TField<T>[];
  onSubmit: (data: T) => {};
  onCancel?: () => any;
  submitText?: string;
  cancelText?: string;
}

function DynamicField<T>(props: TDynamicFieldProps<T>) {
  const { fieldType, label } = props.config;

  switch (fieldType) {
    case 'number':
    case 'text':
    case 'password':
      return <DynamicTextField {...props} />;

    case 'checkbox':
      return <DynamicCheckbox {...props} />;

    case 'select':
      return <DynamicSelectField {...props} />

    case 'autocomplete':
      return <DynamicAutocomplete {...props} />

    case 'date':
      return <DynamicDataField {...props} />

    case 'switch':
      return <DynamicSwitch {...props} />

    case 'comment':
      return <label>{label}</label>

    case 'confirmationField':
      return <DynamicConfirmationField {...props} />

    default:
      return null;
  }
}


function DynamicForm<T>({ config, onSubmit, onCancel, submitText, cancelText }: TDynamicFormProps<T>) {
  const [formData, setFormData] = useState<T>(getInitialValue(config));
  const [errors, setErrors] = useState<TFormError[]>([])
  const [formConfig, setFormConfig] = useState(config)


  const handleInputChange = (value: any, fieldConfig: TField<T>) => {
    if (fieldConfig.onChange) {
      fieldConfig.onChange(value, formData, setFormConfig);
    }
    setFormData((prevData: any) => ({ ...prevData, [fieldConfig.fieldName]: value }));
  };


  const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const validation = validateForm(config, formData)
    if (validation.length > 0) {
      setErrors(validation)
      return
    }
    onSubmit(formData)
  }


  const cancelHandler = () => {
    setFormData({} as T)
    onCancel && onCancel()
  }

  useEffect(()=>{
  },[formConfig])

  return (
    <Form onSubmit={submitHandler}>

      <Fieldset>
        {formConfig.map(fieldConfig => {
          const { isVisible, fullWidth, width, ...fieldProps } = fieldConfig
          const fieldError = errors.filter(error => error.fieldName === fieldConfig.fieldName)[0]
          if (isVisible) {
            return (
              <div key={fieldConfig.fieldName as string} style={{ width: fullWidth ? 'calc(100% - 10px)' : (Boolean(width) ? width : 'calc(50% - 10px)') }}>
                <DynamicField
                  config={fieldProps}
                  formData={formData}
                  onChange={(value: any): any => {
                    handleInputChange(value, fieldConfig)
                  }}
                  error={fieldError ? fieldError.error : ''}
                />
              </div>
            )
          }
        })}
      </Fieldset>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', gap: '20px' }}>
        {onCancel ? <Button type='button' variant='outlined' color='secondary' onClick={cancelHandler}>{cancelText || 'Cancel'}</Button> : <></>}
        <Button variant='contained' type='submit'>{submitText || 'Submit'}</Button>
      </div>

    </Form>
  )
}

export default DynamicForm



function getInitialValue<T>(config: TField<T>[]): T {
  return config.reduce((initialValues, field) => {
    return field.value != undefined ?
      { ...initialValues, [field.fieldName]: field.value } :
      field.initialValue !== undefined ?
        { ...initialValues, [field.fieldName]: field.initialValue } :
        initialValues
  }, {} as T)
}

function validateForm<T>(config: TField<T>[], data: any) {
  const errors = []
  for (let fieldConfig of config) {
    const { fieldName, error } = validateField(fieldConfig, data[fieldConfig.fieldName])

    // Type of error property would be 'boolean' only if there is no error, so we push only in case of error.
    if (typeof error !== 'boolean') errors.push({ fieldName, error })
  }
  return errors
}

function validateField<T>(field: TField<T>, value: any) {
  const { validation, fieldType, label } = field

  const fieldName: string = field.fieldName as string

  if (typeof validation !== 'object') return fieldError(fieldName)

  const { required, minLength, maxLength, min, max, contain, regex, email } = validation

  const requiredError = required !== undefined ? checkRequired(Boolean(required), value, fieldName, label) : false
  if (requiredError !== false) return requiredError

  const emailError = email !== undefined ? checkEmailError(email, value, fieldName, label) : false
  if (emailError !== false) return emailError

  const containError = contain !== undefined ? checkContainError(contain, value, fieldName, label) : false
  if (containError !== false) return containError

  const regexError = regex !== undefined ? checkRegexError(regex, value, fieldName, label) : false
  if (regexError !== false) return regexError

  switch (fieldType) {
    case 'text':
    case 'password':
    case 'select':
    case 'autocomplete':
      const minLengthError = typeof minLength === 'number' ? checkMinLengthError(minLength, value, fieldName, label) : false
      if (minLengthError !== false) return minLengthError

      const maxLengthError = typeof maxLength === 'number' ? checkMaxLengthError(maxLength, value, fieldName, label) : false
      if (maxLengthError !== false) return maxLengthError

      return fieldError(fieldName)

    case 'number':
      const minError = typeof min === 'number' ? checkMinError(min, value, fieldName, label) : false
      if (minError !== false) return minError

      const maxError = typeof max === 'number' ? checkMaxError(max, value, fieldName, label) : false
      if (maxError !== false) return maxError

      return fieldError(fieldName)

    default:
      return fieldError(fieldName)
  }

}

function checkRequired(required: boolean, value: any, field: string, label: string) {
  if (required && (typeof value === 'undefined' || value === null)) {
    return fieldError(field, `${label} cannot be empty.`)
  }
  return false
}

function checkEmailError(email: boolean, value: any, field: string, label: string) {
  if (email && !emailRegex.test(value)) {
    return fieldError(field, `${label} is invalid.`)
  }
  return false
}

function checkMinLengthError(minLength: number, value: any, field: string, label: string) {
  if (typeof value === 'string' && value.length < minLength) {
    return fieldError(field, `${label} must contain minimum ${minLength} characters.`)
  }
  return false
}

function checkMaxLengthError(maxLength: number, value: any, field: string, label: string) {
  if (typeof value === 'string' && value.length > maxLength) {
    return fieldError(field, `${label} cannot contain more than ${maxLength} characters.`)
  }
  return false
}

function checkMinError(min: number, value: any, field: string, label: string) {
  if (typeof value === 'number' && value < min) {
    return fieldError(field, `Value of ${label} cannot be less than ${min}.`)
  }
  return false
}

function checkMaxError(max: number, value: any, field: string, label: string) {
  if (typeof value === 'number' && value > max) {
    return fieldError(field, `Value of ${label} cannot be more than ${max}.`)
  }
  return false
}

function checkContainError(allowed: string[] | number[] | string, value: any, field: string, label: string) {
  if ((typeof value === 'number' || typeof value === 'string') && !allowed.includes(value as never)) {
    return fieldError(field, `Value of ${label} is invalid.`)
  }
  return false
}

function checkRegexError(regex: RegExp, value: any, field: string, label: string) {
  if (!regex.test(value)) {
    return fieldError(field, `Value of ${label} is invalid.`)
  }
  return false
}

function fieldError(fieldName: string, error?: string) {
  return { fieldName, error: error || false }
}


function DynamicTextField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldType, fieldName, label } = config;
  return <TextField
    type={fieldType}
    onChange={(e) => onChange(e.target.value)}
    fullWidth label={label}
    value={formData[fieldName]}
    error={Boolean(error)}
    helperText={Boolean(error) && error}
    size='small'
  />
}

function DynamicConfirmationField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldType, fieldName, label, onConfirm, isVerified } = config;
  const getValue = () => formData[fieldName]
  return <TextField
  size='small'
    type={fieldType}
    onChange={(e) => onChange(e.target.value)}
    fullWidth label={label}
    value={getValue()}
    error={Boolean(error)}
    helperText={Boolean(error) && error}
    InputProps={{
      endAdornment: <IconButton onClick={() => !isVerified && onConfirm!(getValue(), formData)}>
        {isVerified ? <VerifiedUser color='success' /> : <Replay />}
      </IconButton>
    }}
  />
}

function DynamicSelectField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { options, label } = config;
  const fieldName = config.fieldName
  const stringFieldName = fieldName as string
  return <FormControl fullWidth size='small'>
    <InputLabel id={`${stringFieldName}_label`}>{label}</InputLabel>
    <Select
      onChange={(e) => onChange(e.target.value)}
      labelId={`${stringFieldName}_label`}
      id={stringFieldName}
      label={label}
      value={formData[fieldName]}
      error={Boolean(error)}
    >
      {options?.map((option, index) => (
        <MenuItem
          selected={option.value === formData[fieldName]} key={index} value={option.value}>{option.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
}

function DynamicAutocomplete<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { options, fieldName, label } = config;
  return <Autocomplete
  size='small'
    fullWidth
    options={options!}
    value={options?.filter(option => option.value === formData[fieldName])[0] }
    getOptionLabel={(option) => option.label}
    onChange={(event, newValue) => onChange(newValue?.value)}
    renderInput={(params) => <TextField
      label={label}
      error={Boolean(error)}
      helperText={error}
      {...params}
    />}
  />
}

function DynamicDataField<T>({ config, formData, onChange, error }: TDynamicFieldProps<T>) {
  const { fieldName, label } = config;
  return <LocalizationProvider dateAdapter={AdapterMoment}>
    <MobileDatePicker
      slotProps={{
        textField: { fullWidth: true, error: Boolean(error), helperText: error, size:'small' },
        field: {
          InputProps: {
            endAdornment: <IconButton><CalendarIcon /></IconButton>
          }
        }
      }}
      label={label}
      onChange={(value) => onChange(value)}
      value={moment(formData[fieldName] as MomentInput)}
    />
  </LocalizationProvider>
}

function DynamicCheckbox<T>({ config, formData, onChange }: TDynamicFieldProps<T>) {
  const { fieldName, label } = config;
  return <FormControlLabel
    sx={{ color: "#7f7d76" }}
    control={
      <Checkbox
      size='small'
        onChange={(e, checked) => onChange(checked)}
        checked={Boolean(formData[fieldName])}
      />
    }
    label={label}
  />
}

function DynamicSwitch<T>({ config, formData, onChange }: TDynamicFieldProps<T>) {
  const { fieldName, label } = config;
  return <FormControlLabel
    control={<Switch
      size='small'
      onChange={(e, checked) => onChange(checked)}
      checked={Boolean(formData[fieldName])}
    />}
    label={label}
  />
}

